// =========================================================================
// HelpyDoc · Asistente automático del programa (chatbot RAG) — HD-045 B4
// =========================================================================
// Piezas PURAS (sin red, sin React) del cliente del asistente:
//   - el flag de producto de CLIENTE (`VITE_ASISTENTE`, patrón de
//     `lib/biblioteca.ts::isBibliotecaEnabled`);
//   - la memoria local de "ya viste el aviso previo al primer uso" (R10);
//   - cómo clasificar una `RespuestaAsistente` para pintar el mensaje
//     (normal / derivación / urgencia / sin fragmento);
//   - el generador de respuestas MOCK para `VITE_DATA_MODE=mock`.
//
// El pipeline REAL (puerta clínica determinista, recuperación, LLM
// inyectable) vive enteramente en el backend
// (services/helpydoc-backend/src/modules/asistente/, packages/contracts/src/
// asistente.ts — bloques B1-B3 de este mismo spec). Nada de lo que hay aquí
// lo reimplementa ni lo sustituye: `respuestaAsistenteMock` es solo para que
// la pantalla tenga algo determinista que enseñar en local/demo, sin red y
// sin gasto (regla dura #8 del encargo).
//
// Flag de CLIENTE (`VITE_ASISTENTE`) y flag de SERVIDOR (`ASISTENTE_ENABLED`)
// son DOS interruptores independientes (R14): hacen falta los dos en
// 'on'/'true' para que una persona real llegue a hablar con el asistente.
// Ninguno de los dos se toca en `.env.production` desde este bloque.
// =========================================================================

import type {
  CategoriaPuertaClinica,
  PreguntaAsistenteInput,
  RespuestaAsistente,
} from '@/services/api-client';

/**
 * Flag de producto (cliente). Default (incl. producción) = OFF hasta que
 * Carlos decida D7 (encender en producción). `VITE_ASISTENTE` no existe hoy
 * en ningún `.env*` del repo — ausente se lee exactamente igual que 'off'.
 */
export function isAsistenteEnabled(): boolean {
  return import.meta.env.VITE_ASISTENTE === 'on';
}

// ── R10 · aviso informativo previo al primer uso ──────────────────────────
// Recordado SOLO en este navegador (localStorage, per-viewer): no es un
// consentimiento firmado ni un registro (eso es HD-040/ConsentSignature, un
// flujo distinto); es la pantalla informativa de R10 que no hace falta
// repetir en cada sesión. Si el almacenamiento no está disponible (privado,
// bloqueado…) se prefiere mostrar el aviso DE MÁS, nunca de menos.
const LS_AVISO_VISTO = 'helpydoc_asistente_aviso_visto';

export function haVistoAvisoAsistente(): boolean {
  try {
    return localStorage.getItem(LS_AVISO_VISTO) === '1';
  } catch {
    return false;
  }
}

export function marcarAvisoAsistenteVisto(): void {
  try {
    localStorage.setItem(LS_AVISO_VISTO, '1');
  } catch {
    /* per-viewer únicamente: si no se puede guardar, se repite el aviso — no rompe nada más */
  }
}

// ── Clasificación de la respuesta, para elegir cómo pintar el mensaje ─────
export type TipoMensajeAsistente = 'normal' | 'derivacion' | 'urgencia' | 'sin-fragmento';

/**
 * Deriva el tipo de mensaje SOLO de los campos que ya manda el backend
 * (`decisionPuerta.disparada`/`categoria`, `sinFragmentoSuficiente`,
 * `fueraDeAmbito`) — no vuelve a evaluar ningún texto. Ver el contrato exacto
 * en `docs/relevos/BITACORA-HD-045-ASISTENTE.md`, entrada
 * `CONTRATO:endpoint-preguntas`.
 */
export function clasificarRespuesta(respuesta: RespuestaAsistente): TipoMensajeAsistente {
  if (respuesta.decisionPuerta.disparada) {
    return respuesta.decisionPuerta.categoria === 'urgencia' ? 'urgencia' : 'derivacion';
  }
  if (respuesta.sinFragmentoSuficiente || respuesta.fueraDeAmbito) return 'sin-fragmento';
  return 'normal';
}

// ── Mock (VITE_DATA_MODE=mock) — heurística de DEMO, NO una puerta clínica ──
// Deliberadamente pequeña y aproximada: solo existe para que la pantalla
// tenga los 4 estados de UI (normal/derivación/urgencia/sin-fragmento) que
// enseñar sin red. La única puerta clínica que protege de verdad
// (`evaluarPuertaClinica`, 7 reglas versionadas + banco de 64 preguntas, R15)
// vive en `packages/contracts/src/asistente.ts` y corre en el backend, nunca
// aquí — este fichero no la importa ni la imita a propósito.
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function contieneAlguna(textoNormalizado: string, disparadores: readonly string[]): boolean {
  return disparadores.some((d) => textoNormalizado.includes(d));
}

const DISPARADORES_URGENCIA_MOCK = [
  '112', '024', 'quiero morir', 'quitarme la vida', 'suicid', 'autolesion',
  'no puedo respirar', 'dolor en el pecho',
] as const;

const DISPARADORES_CLINICOS_MOCK = [
  'diagnostic', 'que enfermedad tengo', 'tratamiento', 'medicament', 'dosis',
  'receta', 'sintoma', 'me duele', 'diabetes tipo', 'cancer', 'analitica',
] as const;

const CATEGORIA_MOCK_CLINICO: CategoriaPuertaClinica = 'diagnostico';

/**
 * Genera una `RespuestaAsistente` determinista, sin red, para
 * `VITE_DATA_MODE=mock`. Los tres textos de demo llevan su propia marca de
 * mock: no pretenden ser el texto final de D3 (ese vive en el backend,
 * `TEXTO_URGENCIA_112_024`/`TEXTO_DERIVACION_GENERICA`) ni el corpus real de
 * D2 (ese sigue en `estado: borrador`, ver `content/asistente/corpus.yaml`).
 */
export function respuestaAsistenteMock(input: PreguntaAsistenteInput): RespuestaAsistente {
  const conversacionId = input.conversacionId ?? `mock-${Date.now()}`;
  const normalizado = normalizar(input.texto);

  if (contieneAlguna(normalizado, DISPARADORES_URGENCIA_MOCK)) {
    return {
      texto:
        'Esto suena a una emergencia. Por favor, contacta ahora mismo con el 112 (emergencias) ' +
        'o el 024 (línea de atención a la conducta suicida). Este asistente no puede ayudarte ' +
        'con esto — un profesional sí. [MOCK de demostración — el texto final lo decide Carlos, D3]',
      citas: [],
      decisionPuerta: { disparada: true, categoria: 'urgencia', origen: 'entrada' },
      conversacionId,
    };
  }

  if (contieneAlguna(normalizado, DISPARADORES_CLINICOS_MOCK)) {
    return {
      texto:
        'Esto es una consulta clínica y este asistente no puede responderla: solo resuelve ' +
        'dudas sobre el programa, sus contenidos y el uso de la app. Coméntalo con tu equipo ' +
        'asistencial. [MOCK de demostración — el texto final lo decide Carlos, D3]',
      citas: [],
      decisionPuerta: { disparada: true, categoria: CATEGORIA_MOCK_CLINICO, origen: 'entrada' },
      conversacionId,
    };
  }

  if (normalizado.trim().length < 4) {
    return {
      texto: 'No tengo eso en el material del programa todavía.',
      citas: [],
      decisionPuerta: { disparada: false },
      sinFragmentoSuficiente: true,
      conversacionId,
    };
  }

  return {
    texto:
      'El método del plato reparte tu plato en mitad de verduras, un cuarto de proteína y un ' +
      'cuarto de hidratos de carbono. (Respuesta de ejemplo en modo mock — el corpus real está ' +
      'pendiente de aprobación, decisión D2.)',
    citas: [{
      fragmentoId: 'mock-metodo-plato',
      tituloFuente: 'Método del plato (contenido de ejemplo, no el corpus aprobado)',
      version: 'mock-v1',
    }],
    decisionPuerta: { disparada: false, origen: 'salida' },
    conversacionId,
  };
}
